
import React, { memo, useCallback, useMemo, useState } from "react";
import { Card, Form, Button } from "react-bootstrap";
import ShareControlModal from "./ShareControlModal";


function getFileNameFromUrl(url, removeExtension = false) {
    if (!url) return "";

    const fullName = url.split("/").pop(); // filename with extension

    if (!removeExtension) {
        return fullName; // return as it is
    }

    // Remove extension
    const parts = fullName.split(".");
    parts.pop(); // remove last extension
    return parts.join(".");
}

function QuestionCard({
    question,
    questionId,
    answered_by,
    shared_with_emails,
    index,
    domain,
    domainName,
    response,
    onChange,
    onFileUpload,
    onRemoveFile,
    onCheckWithNova,
    checkingId,
    submitQuestionAnswerHandler,
    resMsg,
    error,
    messageQuestionId,
    controlTitle,
    loading,
    reviewer_adjustment,
    reviewer_email,
    reviewer_remark,
    submissionStatus,
    hideShareButton = false,
    onRefreshQuestions,
}) {
    const qid = question?.control_question_id;
    const [shareModal, setShareModal] = useState(false);
    const resp = useMemo(() => {
        //Radio Data
        const fallbackAnswer =
            question?.answer_response ||
            "";
        //Textarea data
        const fallbackComments =
            question?.user_answer ||
            "";
        return {
            answer: response?.answer ?? fallbackAnswer ?? "",
            comments: response?.comments ?? fallbackComments ?? "",
            files: response?.files ?? [],
            submitted:
                response?.submitted ??
                !!(
                    (fallbackAnswer && fallbackAnswer.trim() !== "") ||
                    (fallbackComments && fallbackComments.trim() !== "")
                )
        };
    }, [question, response]);




    const showMessage = messageQuestionId === qid && (resMsg || error);
    const isSaving = loading && messageQuestionId === qid;
    const hasValues =
        (resp?.answer && resp.answer.trim() !== "") ||
        (resp?.comments && resp.comments.trim() !== "") ||
        (resp?.files && resp.files.length > 0) ||
        (resp?.file_urls && resp.file_urls.length > 0);
    const isEditDisabled = submissionStatus && submissionStatus !== "draft";

    // Check if answer was already submitted before (from API response)
    const hasExistingAnswer =
        question?.answer_response ||
        question?.user_response ||
        question?.user_answer ||
        question?.answer ||
        (question?.question_answer && (question.question_answer.answer_response || question.question_answer.user_response || question.question_answer.user_answer || question.question_answer.answer)) ||
        (question?.answer_details && (question.answer_details.answer_response || question.answer_details.user_response || question.answer_details.user_answer || question.answer_details.answer)) ||
        resp?.submitted;

    const disableSave = isSaving || isEditDisabled;
    const buttonLabel = (() => {
        if (isSaving) return "Saving...";
        if (messageQuestionId === qid && resMsg) return hasExistingAnswer ? "Updated" : "Saved";
        if (hasExistingAnswer) return "Update";
        return "Save";
    })();

    //  FIX 4: Optimize radio button change handler
    const handleRadioChange = useCallback((e) => {
        const value = e.target.value;
        onChange(qid, "answer", value);
    }, [qid, onChange]);

    //  FIX 5: Optimize textarea change handler
    const handleTextareaChange = useCallback((e) => {
        const value = e.target.value;
        onChange(qid, "comments", value);
    }, [qid, onChange]);

    //  FIX 6: Optimize file upload handler
    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            onFileUpload(qid, file, domain?.organization?.id || domain?.id);
        }
        // Reset file input
        e.target.value = '';
    }, [qid, domain?.organization?.id, onFileUpload]);

    //  FIX 7: Optimize Nova check handler
    const handleCheckNova = useCallback(() => {
        onCheckWithNova(qid);
    }, [qid, onCheckWithNova]);

    //  FIX 8: Optimize submit handler
    const handleSubmit = useCallback(() => {
        submitQuestionAnswerHandler(qid);
    }, [qid, submitQuestionAnswerHandler]);

    //  FIX 9: Optimize file remove handler
    const handleRemoveFile = useCallback((idx) => {
        onRemoveFile(qid, idx);
    }, [qid, onRemoveFile]);

    const closeShareModal = () => {
        setShareModal(false)
    }

    return (
        <Card className="questionnaire_card p-3 mb-2 shadow-sm">
            {console.log(domain, 'domaindomaindomain')}
            <div className="d-flex gap-2 align-items-center mb-2">
                <span className="color_white fw-semibold font_20">  Domain Name: </span>
                <p className="text-uppercase text-warning  mb-0 font_12">
                    {domainName}
                </p>
            </div>
            <div className="d-flex gap-2 align-items-center mb-2">
                <span className="color_white fw-semibold font_20">  Control Name: </span>{controlTitle && (
                    <p className="text-uppercase text-warning  mb-0 font_12">
                        {controlTitle}
                    </p>
                )}
            </div>
            <h5 className="color_white fw-semibold mb-1 d-flex justify-content-between align-items-start">
                {index + 1}. {question?.question_text || question?.question?.question_text}
                <div className="d-flex gap-2">
                    <span className="badge px-3 py-2 bg-success">{question?.priority || question?.question?.priority}</span>
                    {!hideShareButton && (
                        <Button
                            onClick={() => setShareModal(true)}
                            variant="primary"
                            size="sm"
                            disabled={isEditDisabled}
                        >
                            Sharing
                        </Button>
                    )}
                </div>
            </h5>

            <p className="text-info mb-2">
                <span className="font_600">Evidence Required: </span>
                {question?.evidence_required?.join(", ") || question?.question?.evidence_required?.join(", ")}
            </p>

            {question?.evidence_submitted?.[0]?.url && (
                <p className="text-white mb-2">
                    <span className="font_600">Evidence Submitted: </span>
                    <span className="file_name">{getFileNameFromUrl(question.evidence_submitted[0].url)}</span>
                </p>
            )}

            <p>
            </p>
            <div className="d-flex gap-4 mb-3">
                <span className="badge bg-danger px-3 py-2">Severity: {question?.severity || question?.question?.severity}</span>
                {/* <span className="badge bg-warning text-dark px-3 py-2">Weight: {question?.weight}</span> */}
            </div>

            {/*  FIX 10: Improved radio buttons with proper name attribute */}
            <div className="d-flex gap-4 mb-2 color_white">
                {["Yes", "No", "Partial"].map((opt) => (
                    <Form.Check
                        key={opt}
                        inline
                        type="radio"
                        label={opt}
                        id={`radio-${qid}-${opt}`} // Unique ID
                        name={`question-${qid}`} //  Simplified name
                        value={opt}
                        checked={resp.answer === opt}
                        onChange={handleRadioChange}
                        disabled={isEditDisabled}
                    />
                ))}
            </div>

            {/*  FIX 11: Optimized textarea */}
            <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add comments or details..."
                className="questionnaire_input color_white border-0 mb-2"
                value={resp.comments || ""}
                onChange={handleTextareaChange}
                disabled={isEditDisabled}
            />
            {messageQuestionId === qid && resMsg && (
                <p className="text-success mb-1" aria-live="polite">{resMsg}</p>
            )}
            {messageQuestionId === qid && error && (
                <p className="text-danger mb-0" aria-live="polite">{error}</p>
            )}

            <div className="d-flex justify-content-end">
                {answered_by ? <small className="text-white mb-4"><b>Answered By:</b> {answered_by}</small> : ''}
            </div>




            {
                (reviewer_email || reviewer_adjustment || reviewer_remark) && (
                    <div>
                        <h4 className="color_white fw-semibold mb-2">Review Info</h4>
                        <div className="cm_reviewer_details mb-4">
                            <p>Reviewer Email: <b>{reviewer_email || "N/A"}</b></p>
                            <p>Reviewer Adjustment: <b>{reviewer_adjustment || "N/A"}</b></p>
                            <p>Reviewer Remark: <b>{reviewer_remark || "N/A"}</b></p>
                        </div>
                    </div>
                )
            }

            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                    <span className="color_white fw-semibold">Attach Policy:</span>
                    <Button
                        variant="outline-info"
                        size="sm"
                        className={isEditDisabled ? "disabled" : ""}
                        as="label"
                        htmlFor={`file-${qid}`}
                        disabled={isEditDisabled}
                    >
                        Upload
                    </Button>
                </div>

                <div className="d-flex gap-2 justify-content-between align-items-center">
                    <Button
                        variant="outline-info"
                        size="sm"
                        disabled={checkingId === qid || isEditDisabled}
                        onClick={handleCheckNova}
                    >
                        {checkingId === qid ? "Checking..." : "Check with Nova"}
                    </Button>
                    <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleSubmit}
                        disabled={disableSave}
                    >
                        {buttonLabel}
                    </Button>

                </div>
                <Form.Control
                    id={`file-${qid}`}
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    disabled={isEditDisabled}
                />
            </div>





            {/* FIX 12: Optimized file list */}
            {resp.files?.length > 0 && (
                <div className="mt-2 d-flex  gap-2">
                    {resp.files.map((file, idx) => (
                        <div key={idx} className="d-flex gap-2 align-items-center bg-dark p-1 rounded">
                            <span className="text-light font_10">{file?.name}</span>
                            <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleRemoveFile(idx)}
                                disabled={isEditDisabled}
                            >
                                x
                            </Button>
                        </div>
                    ))}
                </div>


            )}



            <ShareControlModal
                shared_with_emails={shared_with_emails || []}
                show={shareModal}
                onHide={closeShareModal}
                controlQuestionId={questionId}
                onRefreshQuestions={onRefreshQuestions}
            />
        </Card>
    );
}

// FIX 13: Custom comparison function for React.memo
function areEqual(prevProps, nextProps) {
    // Only re-render if these specific props change
    return (
        prevProps.question?.control_question_id === nextProps.question?.control_question_id &&
        prevProps.response === nextProps.response &&
        prevProps.checkingId === nextProps.checkingId &&
        prevProps.index === nextProps.index &&
        prevProps.resMsg === nextProps.resMsg &&
        prevProps.error === nextProps.error &&
        prevProps.messageQuestionId === nextProps.messageQuestionId &&
        prevProps.loading === nextProps.loading &&
        prevProps.domain === nextProps.domain &&
        prevProps.controlTitle === nextProps.controlTitle &&
        prevProps.submissionStatus === nextProps.submissionStatus
    );
}

export default memo(QuestionCard, areEqual);